<script setup lang="ts">
import { ref } from 'vue';

import {
  AppAlternativeFilterButton,
  AppIconButton,
  AppLoadingDots,
} from '@libs/ui';

import SearchThisAreaButton from './SearchThisAreaButton.vue';
import MapSettingsDrawer from './MapSettingsDrawer.vue';
import type { MapboxStyleType } from './models';

interface ProductSelectionMapLayoutProps {
  mapLayout: 'desktop' | 'mobile';
  shouldShowSearchThisAreaButton: boolean;
  isDesktopMapFullScreen: boolean;
  activeFiltersCount: number;
  shouldDisplayLoader: boolean;
  mapboxStyleType: MapboxStyleType;
}

const props = defineProps<ProductSelectionMapLayoutProps>();

interface ProductSelectionMapLayoutEmits {
  (eventName: 'toggle-full-screen'): void;
  (eventName: 'search-this-area'): void;
  (eventName: 'open-filters'): void;
  (eventName: 'close-map'): void;
  (eventName: 'mouse-leave'): void;
  (eventName: 'set-mapbox-style', mapboxStyle: MapboxStyleType): void;
}

const emit = defineEmits<ProductSelectionMapLayoutEmits>();

const isDrawerShown = ref<boolean>(false);
</script>

<template>
  <!-- NOTE: Adding focus-out here will cause a bug -->
  <!-- eslint-disable-next-line -->
  <div @mouseleave="emit('mouse-leave')" class="product-selection-map-layout">
    <!-- ALL VIEWPORTS -->
    <div class="search-this-area-button-wrapper">
      <SearchThisAreaButton
        :visible="
          props.shouldShowSearchThisAreaButton && !props.shouldDisplayLoader
        "
        @click="emit('search-this-area')"
      />
    </div>
    <!-- END OF ALL VIEWPORTS -->

    <!-- DESKTOP -->
    <AppIconButton
      v-if="props.mapLayout === 'desktop'"
      class="action-button-desktop"
      type="secondary"
      state="enabled"
      size="medium"
      :ariaLabel="$t('a11y.open_image_gallery')"
      :icon="{
        name: props.isDesktopMapFullScreen ? 'close' : 'zoom',
      }"
      @click="emit('toggle-full-screen')"
    />
    <!-- END OF DESKTOP -->

    <!-- MOBILE -->
    <div v-if="props.shouldDisplayLoader" class="loading-dots-wrapper-mobile">
      <AppLoadingDots type="black" />
    </div>

    <div v-if="props.mapLayout === 'mobile'" class="map-header-mobile">
      <div class="actions">
        <AppIconButton
          type="modern"
          state="enabled"
          :ariaLabel="$t('a11y.show_map_settings')"
          size="small"
          :icon="{
            name: 'layersOutlined',
          }"
          @click="isDrawerShown = true"
        />

        <AppAlternativeFilterButton
          :text="$t('filter.header')"
          class="filters-button"
          type="modern"
          :compact="false"
          :active-filters-amount="props.activeFiltersCount"
          :show-filters-aria-label="$t('a11y.show_filters')"
          @click="emit('open-filters')"
        />
      </div>

      <AppIconButton
        type="text"
        state="enabled"
        size="medium"
        :icon="{ name: 'close' }"
        :ariaLabel="$t('a11y.close_panel')"
        @click="emit('close-map')"
      />

      <MapSettingsDrawer
        v-if="isDrawerShown"
        :map-type="props.mapboxStyleType"
        @close="isDrawerShown = false"
        @change-map-type="emit('set-mapbox-style', $event)"
      />
    </div>
    <!-- END OF MOBILE -->

    <!-- SLOTS -->
    <div class="map" :class="{ mobile: props.mapLayout === 'mobile' }">
      <slot name="map" />
    </div>
    <div class="product-card">
      <slot name="product-card" />
    </div>
    <!-- END OF SLOTS -->
  </div>
</template>

<style scoped lang="scss">
.product-selection-map-layout {
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 3;

  .action-button-desktop {
    position: absolute;
    top: var(--spacing-12);
    right: var(--spacing-12);
    z-index: 3;
  }

  .search-this-area-button-wrapper {
    position: absolute;
    top: var(--spacing-32);
    left: 50%;
    transform: translateX(-50%);
    z-index: 3;

    @include for-less-or-equal-than-1200-px {
      top: var(--spacing-80);
    }
  }

  .loading-dots-wrapper-mobile {
    border-radius: var(--border-radius-8);
    background-color: var(--color-white);
    height: 36px;
    box-shadow: var(--shadow-elevation-20);

    position: absolute;
    top: var(--spacing-80);
    left: 50%;
    transform: translateX(-50%) scale(1.2);
    z-index: 3;

    display: flex;
    align-items: center;
    justify-content: center;
  }

  .map-header-mobile {
    position: absolute;
    z-index: 3;
    left: 0;
    top: 0;
    height: 60px;
    width: 100%;
    background-color: var(--color-white);

    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 var(--spacing-12);
    border-bottom: 1px solid var(--color-neutral-30);
    box-shadow: var(--shadow-elevation-20);

    .actions {
      display: flex;
      align-items: center;
      gap: var(--spacing-12);
    }
  }

  .product-card {
    position: absolute;
    bottom: var(--spacing-16);
    left: 50%;
    transform: translateX(-50%);
    z-index: 3;
  }

  .map {
    z-index: 2;
    position: relative;

    &.mobile {
      height: calc(100vh - 60px);
      top: 60px;
    }
  }
}
</style>
